import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Tag,
  Spin,
  Typography,
  Space,
} from 'antd';
import {
  FileTextOutlined,
  PlusOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../store/auth';
import { getDashboardStats, getTasks } from '../lib/api';
import { getUserDisplayName } from '../lib/user';

const { Title, Text } = Typography;

export default function Dashboard() {
  const { user } = useAuthStore();
  const [stats, setStats] = useState<any>(null);
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [statsData, tasksData] = await Promise.all([
        getDashboardStats(),
        getTasks(),
      ]);
      setStats(statsData);
      setTasks(tasksData.slice(0, 5));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getRoleTitle = () => {
    switch (user?.role) {
      case 'boss':
        return '老闆儀表板';
      case 'manager':
        return '主管儀表板';
      default:
        return '我的儀表板';
    }
  };

  const getPriorityTag = (priority: string) => {
    const config: Record<string, { color: string; label: string }> = {
      high: { color: 'red', label: '高' },
      medium: { color: 'orange', label: '中' },
      low: { color: 'green', label: '低' },
    };
    const { color, label } = config[priority] || config.low;
    return <Tag color={color}>{label}</Tag>;
  };

  const getStatusTag = (status: string) => {
    const config: Record<string, { color: string; label: string }> = {
      unread: { color: 'default', label: '未讀' },
      read: { color: 'blue', label: '已讀' },
      in_progress: { color: 'processing', label: '進行中' },
      completed: { color: 'success', label: '已完成' },
    };
    const { color, label } = config[status] || config.unread;
    return <Tag color={color}>{label}</Tag>;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Boss stats
  const bossStats = [
    { title: '總任務數', value: stats?.totalTasks || 0 },
    { title: '待處理任務', value: stats?.pendingTasks || 0 },
    { title: '本週已交週報', value: stats?.submittedReports || 0 },
    { title: '總人數', value: stats?.totalUsers || 0 },
  ];

  // Manager stats
  const managerStats = [
    { title: '團隊任務數', value: stats?.teamTasks || 0 },
    { title: '已完成任務', value: stats?.completedTasks || 0 },
    { title: '本週已交週報', value: stats?.submittedTeamReports || 0 },
    { title: '待繳週報', value: stats?.pendingTeamReports || 0 },
  ];

  // Employee stats
  const employeeStats = [
    { title: '我的任務', value: stats?.myTasks || 0 },
    { title: '未讀任務', value: stats?.myPendingTasks || 0 },
    { title: '已完成', value: stats?.myCompletedTasks || 0 },
    {
      title: '本週週報',
      value:
        stats?.currentReportStatus === 'submitted'
          ? '已繳交'
          : stats?.currentReportStatus === 'draft'
          ? '草稿'
          : '未繳交',
    },
  ];

  const currentStats =
    user?.role === 'boss'
      ? bossStats
      : user?.role === 'manager'
      ? managerStats
      : employeeStats;

  return (
    <Space orientation="vertical" style={{ width: '100%' }} size="large">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          {getRoleTitle()}
        </Title>
        <Space>
          <Link to="/reports/new">
            <Button type="primary" icon={<FileTextOutlined />}>
              填寫本週週報
            </Button>
          </Link>
          {(user?.role === 'boss' || user?.role === 'manager') && (
            <Link to="/tasks/new">
              <Button icon={<PlusOutlined />}>指派新任務</Button>
            </Link>
          )}
        </Space>
      </div>

      <Row gutter={[16, 16]}>
        {currentStats.map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card>
              <Statistic title={stat.title} value={stat.value} />
            </Card>
          </Col>
        ))}
      </Row>

      <Card
        title="近期任務"
        extra={
          <Link to="/tasks">
            <Button type="link" icon={<EyeOutlined />}>
              查看全部
            </Button>
          </Link>
        }
      >
        {tasks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <Text type="secondary">目前沒有指派給您的任務</Text>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {tasks.map((task) => (
              <div
                key={task.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px 0',
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, marginBottom: 4 }}>{task.title}</div>
                  <Space>
                    <Text type="secondary">
                      指派者: {getUserDisplayName(task.creator)}
                    </Text>
                    <Text type="secondary">|</Text>
                    <Text type="secondary">
                      截止:{' '}
                      {task.dueDate
                        ? new Date(task.dueDate).toLocaleDateString('zh-TW')
                        : '無期限'}
                    </Text>
                  </Space>
                </div>
                <Space style={{ marginRight: 16 }}>
                  {getPriorityTag(task.priority)}
                  {getStatusTag(task.status)}
                </Space>
                <Link to={`/tasks/${task.id}`}>
                  <Button type="link">查看</Button>
                </Link>
              </div>
            ))}
          </div>
        )}
      </Card>
    </Space>
  );
}
